import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Target, Sparkles, Layers3, BarChart3 } from 'lucide-react';

interface PlanDirecteurTIModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'vue' | 'pourquoi' | 'contenu' | 'approche' | 'exemples' | 'ecosysteme' | 'valeur' | 'processus' | 'demarrer';

export default function PlanDirecteurTIModal({ isOpen, onClose }: PlanDirecteurTIModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('vue');

  if (!isOpen) return null;

  const tabs: { id: TabId; label: string; highlight?: boolean }[] = [
    { id: 'vue', label: 'Vue d’ensemble' },
    { id: 'pourquoi', label: 'Pourquoi' },
    { id: 'contenu', label: 'Contenu' },
    { id: 'approche', label: 'Approche PRISME', highlight: true },
    { id: 'exemples', label: 'Exemples' },
    { id: 'ecosysteme', label: 'Écosystème' },
    { id: 'valeur', label: 'Valeur' },
    { id: 'processus', label: 'Processus' },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-linear-to-br from-slate-950/98 via-blue-950/98 to-slate-950/98 rounded-2xl w-[90vw] h-[90vh] border border-cyan-500/40 shadow-2xl flex flex-col overflow-hidden backdrop-blur-xl">
          <div className="flex items-start justify-between px-6 py-4 border-b border-cyan-500/30 bg-slate-950/70">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-cyan-500/20 border border-cyan-400/70 flex items-center justify-center shadow-lg">
                <Target className="w-6 h-6 text-cyan-200" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">🎯 Stratégie & Gouvernance TI</div>
                <h2 className="text-xl md:text-2xl font-bold text-white">Plan directeur TI</h2>
                <p className="text-xs md:text-sm text-cyan-100/80 mt-1">Aligner vos technologies avec vos objectifs d’affaires</p>
              </div>
            </div>

            <button type="button" onClick={onClose} className="ml-4 inline-flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 border border-white/20 p-2 text-white transition">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 pt-4 border-b border-cyan-500/20 bg-slate-950/90">
            <div className="flex flex-wrap gap-2 pb-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition ${
                    activeTab === tab.id
                      ? 'bg-cyan-500/30 text-white border border-cyan-300/70'
                      : tab.highlight
                        ? 'bg-amber-500/10 text-amber-200 border border-amber-400/30 hover:bg-amber-500/20'
                        : 'bg-white/5 text-cyan-100/80 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 text-sm text-slate-100 bg-slate-950/72">

            {activeTab === 'vue' && (
              <div className="space-y-6">
                <Card title="Vue d’ensemble" icon={<Sparkles className="w-5 h-5 text-cyan-300" />}>
                  <p>Le plan directeur TI est un outil stratégique qui permet d’aligner les technologies avec les objectifs d’affaires de l’entreprise.</p>
                </Card>
                <Card title="Objectifs">
                  <BulletList items={[
                    'Aligner TI et stratégie d’affaires',
                    'Prioriser les investissements',
                    'Réduire les risques',
                    'Optimiser les opérations',
                    'Structurer la transformation numérique',
                  ]} />
                </Card>
                <HighlightBox title="Résultat">
                  Une feuille de route claire sur 1 à 3 ans, adaptée à vos priorités et contraintes.
                </HighlightBox>
              </div>
            )}

            {activeTab === 'pourquoi' && (
              <div className="grid md:grid-cols-2 gap-4">
                <Card title="Enjeux">
                  <BulletList items={[
                    'Décisions TI prises sans vision globale',
                    'Investissements non priorisés',
                    'Manque de visibilité sur les risques',
                    'Difficulté à planifier les budgets',
                    'Pression sur la transformation numérique',
                  ]} />
                </Card>
                <Card title="Valeur">
                  <BulletList items={[
                    'Vision claire et structurée',
                    'Décisions éclairées',
                    'Optimisation des coûts',
                    'Réduction des risques',
                    'Accélération de la croissance',
                  ]} />
                </Card>
              </div>
            )}

            {activeTab === 'contenu' && (
              <Card title="Contenu du plan directeur" icon={<Layers3 className="w-5 h-5 text-cyan-300" />}>
                <BulletList items={[
                  'Portrait actuel (As-Is)',
                  'Analyse des enjeux et risques',
                  'Besoins d’affaires',
                  'Vision cible (To-Be)',
                  'Priorisation des initiatives',
                  'Plan annuel TI',
                  'Plan stratégique 3 ans',
                  'Budget et scénarios',
                  'Indicateurs de performance (KPI)',
                ]} />
              </Card>
            )}

            {activeTab === 'approche' && (
              <div className="space-y-6">
                <HighlightBox title="Approche PRISME" accent="amber">
                  Notre approche PRISME permet de structurer le plan directeur de manière rigoureuse et alignée sur les priorités d’affaires.
                </HighlightBox>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card title="Étapes">
                    <BulletList items={[
                      'Prise de besoins complète (PRISME)',
                      'Analyse des actifs TI',
                      'Évaluation de maturité',
                      'Identification des écarts',
                      'Priorisation stratégique',
                      'Construction du plan',
                    ]} />
                  </Card>
                  <Card title="Livrables">
                    <BulletList items={[
                      'Bilan de santé TI',
                      'Plan annuel',
                      'Plan stratégique 3 ans',
                      'Roadmap détaillée',
                    ]} />
                  </Card>
                </div>
              </div>
            )}

            {activeTab === 'exemples' && (
              <Card title="Exemples d’initiatives">
                <BulletList items={[
                  'Migration vers Microsoft 365',
                  'Implantation d’un CRM ou ERP',
                  'Renforcement de la cybersécurité',
                  'Automatisation de processus',
                  'Structuration des données',
                  'Modernisation de l’infrastructure',
                ]} />
              </Card>
            )}

            {activeTab === 'ecosysteme' && (
              <div className="space-y-6">
                <HighlightBox title="Écosystème">
                  Le plan directeur TI agit comme fondation pour l’ensemble des autres initiatives.
                </HighlightBox>
                <Card title="Liens stratégiques">
                  <BulletList items={[
                    'Cybersécurité → gestion des risques intégrée',
                    'Transformation → priorisation des projets',
                    'Données → structuration et valorisation',
                    'Opérations → optimisation TI',
                    'Expérience → amélioration des outils',
                  ]} />
                </Card>
              </div>
            )}

            {activeTab === 'valeur' && (
              <div className="space-y-6">
                <Card title="Bénéfices" icon={<BarChart3 className="w-5 h-5 text-cyan-300" />}>
                  <BulletList items={[
                    'Réduction des dépenses inutiles',
                    'Meilleure planification budgétaire',
                    'Augmentation de la productivité',
                    'Réduction des incidents TI',
                    'Meilleur retour sur investissement',
                  ]} />
                </Card>
                <HighlightBox title="Positionnement">
                  Un plan directeur TI n’est pas une dépense, c’est un levier de performance.
                </HighlightBox>
              </div>
            )}

            {activeTab === 'processus' && (
              <div className="space-y-6">
                <Card title="Étapes du processus">
                  <BulletList items={[
                    'Rencontres PRISME (5 ateliers)',
                    'Analyse et préparation',
                    'Validation avec le client',
                    'Production des livrables',
                    'Présentation exécutive',
                  ]} />
                </Card>
                <div className="grid md:grid-cols-2 gap-4">
                  <HighlightBox title="Durée">3 à 5 semaines</HighlightBox>
                  <HighlightBox title="Implication client">Faible à modérée (accompagnement structuré)</HighlightBox>
                </div>
              </div>
            )}

          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function Card({ title, children, icon }: { title: string; children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/88 p-5 space-y-3 shadow-lg">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="text-lg font-bold text-white">{title}</h3>
      </div>
      <div className="text-slate-200/90 leading-relaxed">{children}</div>
    </div>
  );
}

function HighlightBox({ title, children, accent = 'emerald' }: { title: string; children: React.ReactNode; accent?: 'emerald' | 'amber' }) {
  const styles = accent === 'amber'
    ? 'border-amber-400/30 bg-amber-950/85 text-amber-50'
    : 'border-emerald-400/30 bg-emerald-950/85 text-emerald-50';

  return (
    <div className={`rounded-2xl border p-5 space-y-2 ${styles}`}>
      <h3 className="text-lg font-bold">{title}</h3>
      <div className="leading-relaxed">{children}</div>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <span className="text-emerald-300 mt-0.5">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

